import { useEffect, useState } from 'preact/hooks';

// Keeps the phone screen on during a game (Screen Wake Lock API). Browsers drop
// the lock whenever the page is hidden, so it's re-requested when it comes back.

const AWAKE_KEY = 'cyp.awake';

interface WakeLockSentinelLike {
  released?: boolean;
  release(): Promise<void>;
}
interface WakeLockLike {
  request(type: 'screen'): Promise<WakeLockSentinelLike>;
}

function wakeLockApi(): WakeLockLike | null {
  return typeof navigator !== 'undefined' && 'wakeLock' in navigator ? (navigator as unknown as { wakeLock: WakeLockLike }).wakeLock : null;
}

export function wakeLockSupported(): boolean {
  return wakeLockApi() !== null;
}

/** On unless this device turned it off. */
export function keepAwakePreference(): boolean {
  try {
    return localStorage.getItem(AWAKE_KEY) !== '0';
  } catch {
    return true;
  }
}

export function setKeepAwakePreference(on: boolean): void {
  try {
    localStorage.setItem(AWAKE_KEY, on ? '1' : '0');
  } catch {
    // Not remembered; fine.
  }
}

/**
 * Holds a screen wake lock while `enabled` and the page is visible. Does
 * nothing where the API is missing. `api` and `doc` are injectable for tests.
 */
export function holdWakeLock(enabled: boolean, api: WakeLockLike | null = wakeLockApi(), doc: Document | undefined = typeof document === 'undefined' ? undefined : document): () => void {
  if (!enabled || !api || !doc) return () => {};
  let lock: WakeLockSentinelLike | null = null;
  let stopped = false;
  const acquire = () => {
    if (stopped || doc.visibilityState !== 'visible' || (lock && !lock.released)) return;
    api.request('screen').then(
      (l) => {
        if (stopped) void l.release().catch(() => {});
        else lock = l;
      },
      () => {
        // Refused (battery saver, not allowed); the screen just sleeps as usual.
      },
    );
  };
  const onVisible = () => {
    if (doc.visibilityState === 'visible') acquire();
  };
  acquire();
  doc.addEventListener('visibilitychange', onVisible);
  return () => {
    stopped = true;
    doc.removeEventListener('visibilitychange', onVisible);
    if (lock) void lock.release().catch(() => {});
    lock = null;
  };
}

export function useWakeLock(enabled: boolean): void {
  useEffect(() => holdWakeLock(enabled), [enabled]);
}

/** The footer switch's state, remembered per device. */
export function useKeepAwakeSetting(): [boolean, (on: boolean) => void] {
  const [on, setOn] = useState(keepAwakePreference);
  return [
    on,
    (next: boolean) => {
      setKeepAwakePreference(next);
      setOn(next);
    },
  ];
}
